/**
 * Ingestion guards — small pure helpers that decide whether to trigger
 * downstream workflow execution after an upload, based on the server's
 * dedup verdict.
 *
 * Architectural intent (I1 Decision 9): documents flagged as duplicates
 * inside a channel's dedup window must NOT progress through the workflow
 * engine until a user manually approves or rejects. The capture-app
 * server enforces this by skipping the OCR-job enqueue; the UI side
 * mirrors the same gate by not calling executeWorkflow.
 *
 * These helpers are factored out of UploadPage.js so the gate logic
 * is unit-testable without a React testbed.
 *
 * Backwards compatibility: responses from older capture-app builds will
 * lack the `ingestion` field entirely. In that case both helpers fall
 * back to "yes, execute" — never silently dropping documents.
 */

/**
 * Decide whether the UI should call executeWorkflow after a single-file
 * upload. Returns false only when the server explicitly told us this
 * upload was a duplicate inside the dedup window.
 *
 * @param {object} response  The /api/capture/upload response body.
 * @returns {boolean}
 */
export function shouldExecuteWorkflowForSingleUpload(response) {
  // Missing ingestion block → old server, default to executing.
  if (!response || !response.ingestion) return true;
  // Gate C: when the user chose "Upload as new" the server processed the file
  // as a fresh document (OCR enqueued) despite the duplicate verdict, so its
  // workflow must run too.
  if (response.ingestion.overrodeDuplicate) return true;
  return response.ingestion.decision !== 'duplicate_within_window';
}

/**
 * From a /api/capture/upload/batch response, return the subset of
 * document IDs that should be passed to executeBatch. Duplicate-flagged
 * documents are filtered out.
 *
 * Each entry in response.documents has shape
 *   { id, filename, status }
 * where status is either 'queued' (proceed) or 'duplicate_pending_review'
 * (skip). The server's per-document status string is the source of truth;
 * the absence of a status field falls back to "proceed" (older server).
 *
 * NOT RETIRED BY B4, and worth saying because it looks like it should be. A
 * duplicate must still not be auto-executed on a batch upload — that is the
 * decision a human is being asked to make. What B4 changes is that the drop is
 * no longer silent in effect: the held documents now appear under Processing >
 * Held with two actions, and UploadPage says so. The filter stays; the dead end
 * it used to lead to is what went away.
 *
 * @param {object} response  The /api/capture/upload/batch response body.
 * @returns {string[]}       Document IDs that should be executed.
 */
export function executableDocumentIdsFromBatch(response) {
  const docs = response?.documents;
  if (Array.isArray(docs) && docs.length > 0) {
    return docs
      .filter((d) => d && d.status !== 'duplicate_pending_review')
      .map((d) => d.id)
      .filter(Boolean);
  }
  // Fallback path mirrors the original UploadPage.js coalesce: prefer
  // explicit document_ids when the documents array is absent.
  if (Array.isArray(response?.document_ids)) {
    return response.document_ids.filter(Boolean);
  }
  return [];
}

/**
 * Gate C (block-and-choose): detect a duplicate "decision required" response
 * from a thrown upload error. captureAPI.uploadFile re-throws a 409 as an Error
 * carrying `.status` and `.serverDetails` (the response body). Returns the
 * existing document descriptor ({ id, filename, created_at, status }) when this
 * is a duplicate-block, or null otherwise (genuine error — let the caller
 * handle it as before).
 *
 * @param {any} err  The error thrown by captureAPI.uploadFile.
 * @returns {{id:string, filename?:string, created_at?:string, status?:string}|null}
 */
export function duplicateChoiceFromUploadError(err) {
  const body = err?.serverDetails;
  if (err?.status === 409 && body?.duplicate && body?.existingDocument?.id) {
    return body.existingDocument;
  }
  return null;
}
